import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Icon, Skeleton } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { useSlots, type Slot } from './api';
import {
  WEEKDAY_SHORT,
  addDays,
  addMonths,
  dayNumber,
  formatDayHeading,
  monthGrid,
  monthTitle,
  todayKey,
} from './dates';
import styles from './SlotPicker.module.css';

export interface SlotPickerProps {
  eventTypeId: string;
  /** Restricts the slots to one advisor; omitted = the type's pool. */
  advisorId?: string | undefined;
  /** `internal` offers any future time (the booking route checks conflicts). */
  internal?: boolean | undefined;
  value: Slot | null;
  onChange: (slot: Slot) => void;
  /** Day shown first (defaults to the value's day or today). */
  initialDay?: string | undefined;
  /** Compact = the in-panel variant of Beratung 02 (single column times). */
  layout?: 'default' | 'compact' | undefined;
}

/**
 * Month calendar + the free times of the selected day (Terminierung 01 / Buchungsseite pattern): highlighted
 * days have free slots, a slot is one click. Reusable by the contact record's booking editor.
 */
export function SlotPicker({
  eventTypeId,
  advisorId,
  internal = true,
  value,
  onChange,
  initialDay,
  layout = 'default',
}: SlotPickerProps) {
  const today = todayKey();
  const start = initialDay ?? (value ? toBerlinDateKey(value.startsAt) : today);
  const [month, setMonth] = useState(() => `${start.slice(0, 7)}-01`);
  const [day, setDay] = useState<string>(start);
  const range = useMemo(() => {
    const from = month < today ? today : month;
    const to = addDays(addMonths(month, 1), -1);
    return { from, to };
  }, [month, today]);
  const slots = useSlots(
    range.to < today ? null : { eventTypeId, from: range.from, to: range.to, advisorId, internal },
  );

  useEffect(() => {
    if (value) {
      const key = toBerlinDateKey(value.startsAt);
      setDay(key);
      setMonth(`${key.slice(0, 7)}-01`);
    }
  }, [value]);

  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const entry of slots.data?.days ?? []) map.set(entry.date, entry.slots);
    return map;
  }, [slots.data]);
  const weeks = useMemo(() => monthGrid(month), [month]);
  const daySlots = byDay.get(day) ?? [];
  const monthHasSlots = [...byDay.values()].some((list) => list.length > 0);
  const canGoBack = month > `${today.slice(0, 7)}-01`;

  return (
    <div className={clsx(styles.picker, layout === 'compact' && styles.compact)}>
      <div className={styles.monthRow}>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Vorheriger Monat"
          disabled={!canGoBack}
          onClick={() => setMonth(addMonths(month, -1))}
        >
          <Icon icon={ChevronLeft} size={16} />
        </button>
        <span className={styles.monthTitle}>{monthTitle(month)}</span>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Nächster Monat"
          onClick={() => setMonth(addMonths(month, 1))}
        >
          <Icon icon={ChevronRight} size={16} />
        </button>
      </div>
      <div className={styles.weekdays} aria-hidden="true">
        {WEEKDAY_SHORT.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className={styles.days} role="grid" aria-label={monthTitle(month)}>
        {weeks.map((week, index) => (
          <div key={index} role="row" className={styles.week}>
            {week.map((cell) => {
              const available = (byDay.get(cell.key)?.length ?? 0) > 0;
              const selected = cell.key === day;
              return (
                <button
                  key={cell.key}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  aria-label={
                    available ? `${formatDayHeading(cell.key)} · freie Zeiten` : formatDayHeading(cell.key)
                  }
                  disabled={!cell.inMonth || cell.key < today}
                  className={clsx(
                    styles.day,
                    !cell.inMonth && styles.dayOutside,
                    available && styles.dayAvailable,
                    selected && styles.daySelected,
                    cell.key === today && styles.dayToday,
                  )}
                  onClick={() => setDay(cell.key)}
                >
                  {cell.inMonth ? dayNumber(cell.key) : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {slots.isError ? (
        <Banner tone="danger">{errorMessage(slots.error)}</Banner>
      ) : slots.isPending && !slots.data ? (
        <div className={styles.times} aria-busy="true">
          <Skeleton height={32} />
          <Skeleton height={32} />
          <Skeleton height={32} />
        </div>
      ) : (
        <>
          <div className={styles.dayHeading}>{formatDayHeading(day)}</div>
          {daySlots.length === 0 ? (
            <div className={styles.empty}>
              {monthHasSlots
                ? 'An diesem Tag ist nichts frei. Hervorgehobene Tage haben freie Zeiten.'
                : `Im ${monthTitle(month).split(' ')[0] ?? ''} ist nichts mehr frei.`}
            </div>
          ) : (
            <div className={styles.times}>
              {daySlots.map((slot) => {
                const selected = value?.startsAt === slot.startsAt && value.advisorId === slot.advisorId;
                return (
                  <button
                    key={`${slot.startsAt}-${slot.advisorId}`}
                    type="button"
                    aria-pressed={selected}
                    className={clsx(styles.time, selected && styles.timeSelected)}
                    onClick={() => onChange(slot)}
                  >
                    {formatTime(slot.startsAt)}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
